import RootStore from '@App/stores/root-store'
import { action, makeObservable, observable } from 'mobx'
import { WorkModel } from '@Entities/work/work'
import { PublisherFormType } from '@App/enum/publisher-form-type'
import { setItemToArrayByIndex } from '@App/helpers/work-entities/removers'
import { PublisherSideBarState } from '@App/enum/publisher-side-bar-state'
import { PublisherModel } from '@Entities/work/publisher-model'
import { OriginalPublisherModel } from '@Entities/work/original-publisher-model'

class PublisherStepStore {
    private rootStore: RootStore

    private _originalPublisherData: OriginalPublisherModel
    private _administratorPublisherData: PublisherModel
    private _formType: PublisherFormType
    private _editingOP: OriginalPublisherModel
    private _currentOP: OriginalPublisherModel

    constructor(rootStore: RootStore) {
        makeObservable<
            PublisherStepStore,
            '_originalPublisherData' | '_administratorPublisherData' | '_formType' | '_editingOP' | '_currentOP'
        >(this, {
            _originalPublisherData: observable,
            _administratorPublisherData: observable,
            _formType: observable,
            _editingOP: observable,
            _currentOP: observable,
            setFormType: action,
            initAddAP: action,
            initEditOriginalPublisher: action,
            setOriginalPublisherData: action,
            setAdministratorPublisherData: action,
            resetStore: action,
        })

        this.rootStore = rootStore
        this._formType = PublisherFormType.add
        this._originalPublisherData = {
            publisherName: '',
            publisherShare: null,
            ipiNumber: null,
            lastUpdated: '',
            administratorPublishers: [],
            writers: [],
        }
        this._administratorPublisherData = {
            publisherName: '',
            publisherShare: null,
            lastUpdated: '',
            ipiNumber: '',
        }
    }

    get formType(): PublisherFormType {
        return this._formType
    }

    get originalPublisherData() {
        return this._originalPublisherData
    }

    get administratorPublisherData() {
        return this._administratorPublisherData
    }

    get editingOP() {
        return this._editingOP
    }

    public setFormType(formType: PublisherFormType) {
        this._formType = formType
    }

    public initAddAP = (currentOP: OriginalPublisherModel) => {
        this._currentOP = currentOP
        this.rootStore.popupStore.setPublisherSidebarState(PublisherSideBarState.addAP)
    }

    public initEditOriginalPublisher = (editingOP: OriginalPublisherModel) => {
        this._editingOP = editingOP
        this.rootStore.popupStore.setPublisherSidebarState(PublisherSideBarState.editOP)
    }

    public initAddWriterToOriginalPublisher = (editingOP: OriginalPublisherModel) => {
        this._editingOP = editingOP
        this.rootStore.popupStore.setPublisherSidebarState(PublisherSideBarState.addWriterOp)
    }

    public setOriginalPublisherData(originalPublisherData: OriginalPublisherModel) {
        this._originalPublisherData = originalPublisherData
    }

    public setAdministratorPublisherData(administratorPublisherData: PublisherModel) {
        this._administratorPublisherData = administratorPublisherData
    }

    public addOriginalPublisherToDraft = () => {
        const workDraftFromLocal = this.rootStore.workStore.workDraftLocal

        if (!workDraftFromLocal) {
            return
        }

        const originalPublishers = workDraftFromLocal.originalPublishers
        const lastOpId = originalPublishers.length
            ? Number(originalPublishers[originalPublishers.length - 1].localId)
            : 0

        const updatedWorkDraft: WorkModel = {
            ...workDraftFromLocal,
            originalPublishers: [
                ...originalPublishers,
                {
                    ...this._originalPublisherData,
                    amend: true,
                    ipiNumber: this._originalPublisherData.ipiNumber
                        ? this._originalPublisherData.ipiNumber.padStart(11, '0')
                        : null,
                    localId: lastOpId + 1,
                },
            ],
        }

        this.rootStore.workStore.setWorkDraftToLocal(updatedWorkDraft)
        this.rootStore.popupStore.setPublisherSidebarState(PublisherSideBarState.empty)
    }

    public apItemWasAdded = (publisherName: string) => {
        const workDraftFromLocal = this.rootStore.workStore.workDraftLocal

        if (!workDraftFromLocal) {
            return
        }
        return publisherName === this._currentOP.publisherName
    }

    public setFoundAdministratorPublisher = (publisher: PublisherModel) => {
        this.rootStore.workStore.setFoundAdministratorPublisher(publisher, this._currentOP)
    }

    public addAdministratorPublisherToDraft() {
        const workDraftFromLocal = this.rootStore.workStore.workDraftLocal
        if (!workDraftFromLocal) {
            return
        }
        const originalPublishers = workDraftFromLocal.originalPublishers
        const currentOPid = this._currentOP.localId

        const administratorPublishers = this._currentOP.administratorPublishers
        const lastAPid = administratorPublishers.length
            ? Number(administratorPublishers[administratorPublishers.length - 1].localId)
            : 0

        const indexOfOP = originalPublishers.map((op: OriginalPublisherModel) => op.localId).indexOf(currentOPid)

        const newAdministratorPublisher: PublisherModel = {
            localId: lastAPid + 1,
            amend: true,
            publisherName: this._administratorPublisherData.publisherName,
            ipiNumber: this._administratorPublisherData.ipiNumber
                ? this._administratorPublisherData.ipiNumber.padStart(11, '0')
                : null,
            lastUpdated: this._administratorPublisherData.lastUpdated,
            publisherShare: 0,
            hfaPublisherNumber: this._administratorPublisherData.hfaPublisherNumber,
        }

        const updatedCurrentOP: OriginalPublisherModel = {
            ...this._currentOP,
            publisherShare: 0,
            administratorPublishers: [...this._currentOP.administratorPublishers, newAdministratorPublisher],
        }

        const updatedOPArray = setItemToArrayByIndex(originalPublishers, +indexOfOP, updatedCurrentOP)

        const updatedWorkDraft: WorkModel = {
            ...workDraftFromLocal,
            originalPublishers: updatedOPArray,
        }

        this.rootStore.workStore.setWorkDraftToLocal(updatedWorkDraft)
        this.rootStore.popupStore.setPublisherSidebarState(PublisherSideBarState.empty)
    }

    public resetStore() {
        this._formType = PublisherFormType.add
        this._originalPublisherData = {
            publisherName: '',
            publisherShare: null,
            ipiNumber: null,
            lastUpdated: '',
            administratorPublishers: [],
            writers: [],
        }
        this._administratorPublisherData = {
            publisherName: '',
            publisherShare: null,
            lastUpdated: '',
            ipiNumber: '',
        }
    }
}

export default PublisherStepStore
